import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

/**
 * TradePartnerAutocomplete
 *
 * Props:
 *   value       - current text value
 *   onChange     - called with (name, tradePartner) when user types or selects
 *   tpTypes     - comma-separated types to filter, e.g. "Customer" or "Trucker,Vendor"
 *   placeholder  - input placeholder
 *   style        - extra style for the wrapper
 */
const TradePartnerAutocomplete = ({ value, onChange, tpTypes, placeholder, style }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchTP = async (text) => {
    if (text.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams({ q: text });
      if (tpTypes) params.append('tp_type', tpTypes);
      const res = await api.get(`/trade-partners/search?${params}`);
      setResults(res.data || []);
      setShowDropdown(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange(text, null); // pass null for trade partner — user is just typing

    // Debounce search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchTP(text), 250);
  };

  const handleSelect = (tp) => {
    setQuery(tp.name);
    setShowDropdown(false);
    setResults([]);
    onChange(tp.name, tp); // pass the full trade partner object
  };

  const handleFocus = () => {
    if (query.length > 0 && results.length > 0) {
      setShowDropdown(true);
    } else if (query.length > 0) {
      searchTP(query);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // prevent form submission
      if (showDropdown && results.length > 0) {
        handleSelect(results[0]); // select first result
      }
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', ...style }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Search...'}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      {showDropdown && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto',
          marginTop: '2px',
        }}>
          {results.map(tp => (
            <div
              key={tp.id}
              onClick={() => handleSelect(tp)}
              style={{
                padding: '7px 12px', cursor: 'pointer', fontSize: '12px',
                borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px', alignItems: 'center',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
            >
              <span style={{ fontWeight: 600, color: '#2563eb', minWidth: '60px', fontSize: '11px' }}>{tp.code}</span>
              <span style={{ fontWeight: 500, flex: 1 }}>{tp.name}</span>
              {tp.firms_code && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', background: '#fef3c7', color: '#92400e' }}>{tp.firms_code}</span>}
              <span style={{
                fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
                background: '#f1f5f9', color: '#64748b',
              }}>{tp.tp_type}</span>
              {tp.city && <span style={{ color: '#94a3b8', fontSize: '11px' }}>{tp.city}</span>}
            </div>
          ))}
        </div>
      )}
      {showDropdown && loading && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
          padding: '8px 12px', fontSize: '12px', color: '#94a3b8', marginTop: '2px',
        }}>
          Searching...
        </div>
      )}
    </div>
  );
};

export default TradePartnerAutocomplete;
